import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'motion/react';
import {
  ClipboardList,
  Sparkles,
  RefreshCw,
  Save,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Search,
  ChevronRight,
  User,
  Calendar,
  Check,
  FileCode,
  FileText,
  Activity,
  Layers
} from 'lucide-react';

const InternalTestingCheck = () => {
  // State
  const [commits, setCommits] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionName, setSessionName] = useState('June 2nd Merge Prep');
  const [selectedCommitHash, setSelectedCommitHash] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'pending' | 'tested' | 'high'
  const [searchQuery, setSearchQuery] = useState('');
  
  // Loading & Action states
  const [loadingCommits, setLoadingCommits] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [activeTab, setActiveTab] = useState('ai-qa'); // 'ai-qa' | 'git-changes'
  
  // Local caches to prevent unnecessary fetches
  const [diffsCache, setDiffsCache] = useState({}); // hash -> { stats, diff }
  const [aiBlueprintsCache, setAiBlueprintsCache] = useState({}); // hash -> AI JSON result
  
  // Fetch lists on mount
  useEffect(() => {
    fetchSessions();
  }, []);

  // Fetch saved sessions
  const fetchSessions = async () => {
    try {
      const res = await axios.get('/api/internal/testing-sessions');
      if (res.data.success) {
        setSessions(res.data.sessions || []);
        if (res.data.sessions && res.data.sessions.length > 0) {
          // Load the latest session
          loadSavedSession(res.data.sessions[0]);
        } else {
          // Fresh launch - query Git immediately
          fetchChangesFromGit();
        }
      }
    } catch (err) {
      console.error('Failed to load testing sessions:', err);
    }
  };

  // Fetch changes directly from Git repository
  const fetchChangesFromGit = async () => {
    setLoadingCommits(true);
    try {
      const res = await axios.get('/api/internal/git-commits');
      if (res.data.success) {
        const freshId = 'session_' + Date.now();
        const options = { month: 'short', day: 'numeric' };
        const defaultName = `${new Date().toLocaleDateString('en-US', options)} Merge Gatekeeper Checklist`;
        
        setCurrentSessionId(freshId);
        setSessionName(defaultName);
        setCommits(res.data.commits || []);
        setSelectedCommitHash(null);
        setDiffsCache({});
        setAiBlueprintsCache({});
        
        toast.success('Fetched latest dev branch commits successfully!');
        
        // Auto-save the newly created session
        saveSessionState(freshId, defaultName, res.data.commits, {});
      } else {
        toast.error(res.data.error || 'Failed to execute git log command.');
      }
    } catch (err) {
      console.error('Git log fetching exception:', err);
      toast.error('Failed to fetch changes from server Git repository.');
    } finally {
      setLoadingCommits(false);
    }
  };

  // Load a historical session
  const loadSavedSession = (session) => {
    setCurrentSessionId(session.sessionId);
    setSessionName(session.name);
    setCommits(session.commits || []);
    setSelectedCommitHash(null);
    setAiBlueprintsCache(session.aiBlueprintsCache || {});
    setDiffsCache({});
    toast.success(`Loaded session: "${session.name}"`);
  };

  // Save/Update session status
  const saveSessionState = async (id, name, currentCommits, currentAiCache, showToast = false) => {
    if (!id) return;
    
    // Calculate progress percent
    let progress = 0;
    if (currentCommits.length > 0) {
      const testedCount = currentCommits.filter(c => c.status === 'Tested').length;
      progress = Math.round((testedCount / currentCommits.length) * 100);
    }

    const payload = {
      sessionId: id,
      name: name.trim() || 'Untitled Session',
      date: new Date().toISOString(),
      commits: currentCommits,
      progress,
      aiBlueprintsCache: currentAiCache
    };

    try {
      const res = await axios.post('/api/internal/save-testing-session', payload);
      if (res.data.success) {
        setSessions(res.data.sessions || []);
        if (showToast) {
          toast.success('Merge Checklist Session saved successfully!');
        }
      }
    } catch (err) {
      console.error('Session save exception:', err);
      if (showToast) {
        toast.error('Failed to save testing session.');
      }
    }
  };

  // Toggle commit tested checkbox status
  const toggleCommitTested = (hash, isTested) => {
    const updatedCommits = commits.map(c => {
      if (c.hash === hash) {
        return { ...c, status: isTested ? 'Tested' : 'Pending' };
      }
      return c;
    });
    setCommits(updatedCommits);
    
    // Auto save changes in background
    saveSessionState(currentSessionId, sessionName, updatedCommits, aiBlueprintsCache);
  };

  // Fetch detailed diff stats on commit selection
  const selectCommit = async (hash) => {
    setSelectedCommitHash(hash);
    setActiveTab('ai-qa');

    if (diffsCache[hash]) return; // already cached

    try {
      const res = await axios.get(`/api/internal/git-commit-diff/${hash}`);
      if (res.data.success) {
        setDiffsCache(prev => ({
          ...prev,
          [hash]: { stats: res.data.stats, diff: res.data.diff }
        }));
      }
    } catch (err) {
      console.error(`Failed to fetch diff for commit ${hash}:`, err);
    }
  };

  // Call AI to generate testing blueprint
  const generateAIBlueprint = async (hash, subject) => {
    setGeneratingAi(true);
    try {
      const res = await axios.post('/api/internal/generate-testcase', { hash, message: subject });
      if (res.data.success) {
        const blueprint = res.data.data;
        
        // Update AI cache
        const updatedCache = { ...aiBlueprintsCache, [hash]: blueprint };
        setAiBlueprintsCache(updatedCache);
        
        // Update commit impact levels
        const updatedCommits = commits.map(c => {
          if (c.hash === hash) {
            return {
              ...c,
              impactLevel: blueprint.impactLevel || 'Low',
              affectedAreas: blueprint.affectedAreas || []
            };
          }
          return c;
        });
        setCommits(updatedCommits);
        
        // Save session instantly
        saveSessionState(currentSessionId, sessionName, updatedCommits, updatedCache);
        toast.success('AI Testing Blueprint generated successfully!');
      } else {
        toast.error(res.data.error || 'Failed to generate blueprints.');
      }
    } catch (err) {
      console.error('AI blueprint generation failure:', err);
      toast.error('AI blueprint request failed.');
    } finally {
      setGeneratingAi(false);
    }
  };

  // ------------------------------------------------------------
  // ANALYTICS & STATS HELPERS
  // ------------------------------------------------------------
  const totalCommits = commits.length;
  const verifiedCommits = commits.filter(c => c.status === 'Tested').length;
  const pendingCommits = totalCommits - verifiedCommits;
  const progressPercent = totalCommits > 0 ? Math.round((verifiedCommits / totalCommits) * 100) : 0;

  // Filter & search commits
  const filteredCommits = commits.filter(c => {
    if (filterStatus === 'pending' && c.status !== 'Pending') return false;
    if (filterStatus === 'tested' && c.status !== 'Tested') return false;
    if (filterStatus === 'high' && c.impactLevel !== 'High') return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchSubject = (c.subject || '').toLowerCase().includes(query);
      const matchAuthor = (c.author || '').toLowerCase().includes(query);
      const matchHash = (c.hash || '').toLowerCase().includes(query);
      return matchSubject || matchAuthor || matchHash;
    }
    return true;
  });

  const selectedCommit = commits.find(c => c.hash === selectedCommitHash);
  const selectedDiff = diffsCache[selectedCommitHash] || { stats: 'Loading stats...', diff: 'Loading diffs...' };
  const selectedAiBlueprint = aiBlueprintsCache[selectedCommitHash];

  // SVG Circular progress bar variables
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  return (
    <div className="flex flex-col gap-6 w-full min-h-full pb-12">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white rounded-2xl border border-gray-200/80 p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
            <ClipboardList className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Gatekeeper Checklist</span>
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-[10px] font-semibold text-blue-700 font-mono">
                dev ➔ main
              </div>
            </div>
            <input
              type="text"
              value={sessionName}
              onChange={(e) => {
                setSessionName(e.target.value);
                saveSessionState(currentSessionId, e.target.value, commits, aiBlueprintsCache);
              }}
              className="text-xl font-bold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-200 focus:border-blue-500 focus:outline-none transition py-0.5 px-0 w-80 sm:w-96"
              placeholder="Session Name..."
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={fetchChangesFromGit}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition shadow-sm"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
            Fetch Changes
          </button>
          
          <button
            type="button"
            onClick={() => saveSessionState(currentSessionId, sessionName, commits, aiBlueprintsCache, true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#10b981] hover:bg-[#059669] text-sm font-semibold text-white transition shadow-md shadow-[#10b981]/10"
          >
            <Save className="w-4 h-4" />
            Save Session
          </button>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 items-start">
        {/* Left Column: Stats & Saved Sessions */}
        <div className="flex flex-col gap-6 xl:col-span-1">
          {/* Progress Circular Coverage */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-sm flex flex-col items-center">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider self-start mb-4">Verification Progress</h3>
            
            <div className="relative w-40 h-40 flex items-center justify-center my-2">
              <svg className="transform -rotate-90 w-full h-full">
                <circle
                  cx="80"
                  cy="80"
                  r={radius}
                  stroke="rgba(0, 0, 0, 0.04)"
                  strokeWidth="10"
                  fill="transparent"
                />
                <circle
                  cx="80"
                  cy="80"
                  r={radius}
                  stroke="url(#react-progress-gradient)"
                  strokeWidth="10"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  fill="transparent"
                  className="transition-all duration-500"
                />
                <defs>
                  <linearGradient id="react-progress-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#007BFF" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute flex flex-col items-center">
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight leading-none">{progressPercent}%</span>
                <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mt-1.5">Verified</span>
              </div>
            </div>

            <div className="w-full grid grid-cols-3 gap-2 border-t border-gray-100 pt-5 mt-4 text-center">
              <div>
                <p className="text-base font-bold text-gray-900">{totalCommits}</p>
                <p className="text-[10px] text-gray-500 font-medium">Commits</p>
              </div>
              <div>
                <p className="text-base font-bold text-[#10b981]">{verifiedCommits}</p>
                <p className="text-[10px] text-[#10b981]/90 font-medium">Verified</p>
              </div>
              <div>
                <p className="text-base font-bold text-amber-500">{pendingCommits}</p>
                <p className="text-[10px] text-amber-500/90 font-medium">Pending</p>
              </div>
            </div>
          </div>

          {/* Historical Saved Sessions */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-sm flex flex-col">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Saved Sessions</h3>
            <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
              {sessions.length === 0 ? (
                <p className="text-xs text-gray-400 italic text-center py-4">No sessions saved yet.</p>
              ) : (
                sessions.map(s => {
                  const active = s.sessionId === currentSessionId;
                  const dateLabel = new Date(s.date).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  return (
                    <button
                      key={s.sessionId}
                      type="button"
                      onClick={() => loadSavedSession(s)}
                      className={`w-full text-left p-3 rounded-xl border transition flex flex-col gap-1 ${
                        active
                          ? 'bg-blue-50/50 border-blue-200 text-blue-900'
                          : 'bg-gray-50/40 border-gray-200/60 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <span className="text-xs font-bold truncate">{s.name}</span>
                      <div className="flex justify-between items-center text-[10px] text-gray-500">
                        <span>{dateLabel}</span>
                        <span className={`font-semibold ${s.progress === 100 ? 'text-[#10b981]' : ''}`}>
                          {s.progress}% progress
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Center & Right Workspace columns */}
        <div className="xl:col-span-3 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Commit Selection Checklist (Left Section) */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            {/* Filters */}
            <div className="bg-white rounded-2xl border border-gray-200/80 p-4 shadow-sm space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'pending', label: 'Pending' },
                  { id: 'tested', label: 'Verified' },
                  { id: 'high', label: 'High Impact' }
                ].map(btn => (
                  <button
                    key={btn.id}
                    type="button"
                    onClick={() => setFilterStatus(btn.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                      filterStatus === btn.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search commits, authors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 bg-gray-50/50 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none transition"
                />
              </div>
            </div>

            {/* Commit card lists */}
            <div className="space-y-2.5 max-h-[600px] overflow-y-auto pr-1">
              {loadingCommits ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-200/80 shadow-sm">
                  <div className="animate-spin h-6 w-6 border-2 border-gray-100 border-t-blue-600 rounded-full mb-3" />
                  <p className="text-xs">Consulting Git logs. Loading commits...</p>
                </div>
              ) : filteredCommits.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-200/80 shadow-sm text-center px-4">
                  <p className="text-xs">No pending commits matching selection criteria.</p>
                </div>
              ) : (
                filteredCommits.map(commit => {
                  const active = commit.hash === selectedCommitHash;
                  const verified = commit.status === 'Tested';
                  const avatar = commit.author ? commit.author.charAt(0).toUpperCase() : '?';
                  const shortDate = commit.date ? commit.date.substring(0, 16) : 'Unknown';
                  
                  return (
                    <div
                      key={commit.hash}
                      onClick={() => selectCommit(commit.hash)}
                      className={`relative flex gap-3 p-4 rounded-xl border cursor-pointer select-none transition ${
                        active
                          ? 'bg-blue-50/20 border-blue-500/40 shadow-sm'
                          : 'bg-white border-gray-200/80 hover:bg-gray-50/60'
                      }`}
                    >
                      {/* Custom Left Bar Line Indicator */}
                      <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-lg ${
                        verified ? 'bg-[#10b981]' : commit.impactLevel === 'High' ? 'bg-red-500' : 'bg-amber-400'
                      }`} />

                      <div className="flex items-start pt-0.5">
                        <label className="flex items-center justify-center h-4 w-4 relative cursor-pointer" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={verified}
                            onChange={(e) => toggleCommitTested(commit.hash, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </label>
                      </div>

                      <div className="flex-1 min-w-0 space-y-1.5 pr-2">
                        <h4 className={`text-xs font-bold leading-tight truncate ${verified ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                          {commit.subject}
                        </h4>
                        <div className="flex items-center justify-between text-[10px] text-gray-500">
                          <div className="flex items-center gap-1.5 truncate">
                            <span className="w-4 h-4 bg-gray-100 rounded-full border border-gray-200 flex items-center justify-center font-bold text-[8px] text-gray-600">
                              {avatar}
                            </span>
                            <span className="truncate">{commit.author.split(' <')[0]}</span>
                          </div>
                          <span>{shortDate}</span>
                        </div>
                      </div>

                      <div className="flex flex-col gap-1 items-end shrink-0">
                        {verified ? (
                          <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-emerald-50 text-[#10b981] border border-emerald-100 uppercase tracking-wide">
                            Verified
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-amber-50 text-amber-600 border border-amber-100 uppercase tracking-wide">
                            Pending
                          </span>
                        )}
                        {commit.impactLevel && commit.impactLevel !== 'Low' && !verified && (
                          <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wide ${
                            commit.impactLevel === 'High' 
                              ? 'bg-red-50 text-red-500 border border-red-100'
                              : 'bg-amber-50 text-amber-500 border border-amber-100'
                          }`}>
                            {commit.impactLevel} Impact
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Inspection View Details (Right Section) */}
          <div className="lg:col-span-7">
            {!selectedCommit ? (
              <div className="h-full bg-white rounded-2xl border border-gray-200/80 p-8 shadow-sm flex flex-col items-center justify-center text-center gap-4 text-gray-500">
                <div className="h-14 w-14 rounded-2xl bg-gray-50 flex items-center justify-center border border-gray-100 text-gray-400">
                  <ClipboardList className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">No Commit Selected</h3>
                  <p className="text-xs max-w-sm mt-1 leading-normal">
                    Select a commit from the checklist on the left to review plain English summaries, auto-generate step-by-step QA test procedures, and inspect code diffs.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm flex flex-col overflow-hidden">
                {/* Commit Summary Header */}
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex justify-between items-center gap-2 mb-2">
                    <span className="text-[10px] font-mono text-gray-400 tracking-wider">HASH: {selectedCommit.hash}</span>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${
                      selectedCommit.impactLevel === 'High'
                        ? 'bg-red-50 text-red-500 border border-red-100 shadow-sm shadow-red-500/5'
                        : selectedCommit.impactLevel === 'Medium'
                          ? 'bg-amber-50 text-amber-500 border border-amber-100'
                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                    }`}>
                      {selectedCommit.impactLevel || 'Low'} Impact
                    </span>
                  </div>
                  <h2 className="text-base font-bold text-gray-900 leading-snug mb-3">
                    {selectedCommit.subject}
                  </h2>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      <span>{selectedCommit.author}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <span>{selectedCommit.date}</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4 mt-4 flex items-center justify-between">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCommit.status === 'Tested'}
                        onChange={(e) => toggleCommitTested(selectedCommit.hash, e.target.checked)}
                        className="h-4.5 w-4.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs font-semibold text-gray-800">Mark entire commit as "Tested & Verified"</span>
                    </label>
                  </div>
                </div>

                {/* Sub Tab Controls */}
                <div className="flex border-b border-gray-100 px-6 gap-6 bg-white">
                  <button
                    type="button"
                    onClick={() => setActiveTab('ai-qa')}
                    className={`py-3.5 px-1 text-xs font-semibold relative transition ${
                      activeTab === 'ai-qa' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      AI QA Guide & Blueprint
                    </span>
                    {activeTab === 'ai-qa' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('git-changes')}
                    className={`py-3.5 px-1 text-xs font-semibold relative transition ${
                      activeTab === 'git-changes' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <FileCode className="w-3.5 h-3.5" />
                      Files & Code Diff
                    </span>
                    {activeTab === 'git-changes' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                    )}
                  </button>
                </div>

                {/* Tab Context views */}
                <div className="p-6">
                  {activeTab === 'ai-qa' ? (
                    <div>
                      {generatingAi ? (
                        <div className="space-y-4 py-8">
                          <div className="h-6 bg-gray-100 rounded animate-pulse w-48" />
                          <div className="space-y-2">
                            <div className="h-3.5 bg-gray-100 rounded animate-pulse w-full" />
                            <div className="h-3.5 bg-gray-100 rounded animate-pulse w-11/12" />
                            <div className="h-3.5 bg-gray-100 rounded animate-pulse w-3/4" />
                          </div>
                          <div className="h-28 bg-gray-50 border border-gray-100 rounded-xl animate-pulse mt-8" />
                        </div>
                      ) : !selectedAiBlueprint ? (
                        <div className="flex flex-col items-center justify-center text-center p-8 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200/80 gap-3">
                          <div className="h-10 w-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600">
                            <Sparkles className="w-5 h-5 animate-pulse" />
                          </div>
                          <h4 className="text-xs font-bold text-gray-900">Generate AI QA Blueprint</h4>
                          <p className="text-[11px] text-gray-500 max-w-sm">
                            Analyze this commit's structural code diff, write a plain-English explanation, and generate manual UI step-by-step test plans.
                          </p>
                          <button
                            type="button"
                            onClick={() => generateAIBlueprint(selectedCommit.hash, selectedCommit.subject)}
                            className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-xs font-bold text-white transition shadow-sm"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            Generate Testing Plan
                          </button>
                        </div>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0, y: 3 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          {/* Plain English explanation */}
                          <div className="p-5 rounded-2xl bg-gradient-to-r from-blue-50/30 to-emerald-50/10 border border-blue-100/60 shadow-sm space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wider">What this does (Plain English)</span>
                              <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 uppercase tracking-wide">AI Blueprint</span>
                            </div>
                            <p className="text-xs text-gray-800 leading-relaxed font-medium">
                              {selectedAiBlueprint.explanation}
                            </p>
                            {selectedAiBlueprint.affectedAreas && selectedAiBlueprint.affectedAreas.length > 0 && (
                              <div className="flex flex-wrap items-center gap-2 border-t border-blue-100/40 pt-3 mt-2">
                                <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider">Affected:</span>
                                <div className="flex flex-wrap gap-1.5">
                                  {selectedAiBlueprint.affectedAreas.map((area, idx) => (
                                    <span key={idx} className="text-[10px] px-2 py-0.5 bg-white border border-blue-200/50 rounded-lg text-gray-600">
                                      {area}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Step by step manual scenarios */}
                          <div className="space-y-3">
                            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Manual Step-by-Step QA Scenarios</h3>
                            <div className="space-y-3">
                              {selectedAiBlueprint.testCases && selectedAiBlueprint.testCases.map((tc, index) => (
                                <div key={index} className="bg-gray-50 border border-gray-200/80 rounded-xl overflow-hidden shadow-sm">
                                  <div className="px-4 py-3 bg-gray-100/50 border-b border-gray-200/60 flex items-center gap-2">
                                    <div className="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center font-bold text-[10px] text-gray-600">
                                      {index + 1}
                                    </div>
                                    <span className="text-xs font-bold text-gray-900">{tc.title}</span>
                                  </div>
                                  <div className="p-4 space-y-4">
                                    {/* Steps */}
                                    <ul className="space-y-2.5">
                                      {tc.steps && tc.steps.map((step, sIdx) => (
                                        <li key={sIdx} className="flex gap-2 text-xs text-gray-600 leading-normal">
                                          <span className="text-blue-500 font-bold text-[10px]">{sIdx + 1}.</span>
                                          <span>{step}</span>
                                        </li>
                                      ))}
                                    </ul>
                                    {/* Expected */}
                                    <div className="p-3 bg-emerald-50/50 border border-emerald-100 rounded-lg">
                                      <span className="block text-[9px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Expected Result</span>
                                      <p className="text-xs text-gray-800 leading-relaxed font-medium">
                                        {tc.expectedResult}
                                      </p>
                                    </div>
                                    {/* Context */}
                                    {tc.technicalContext && (
                                      <div className="pl-3 border-l-2 border-purple-500 text-[10px] text-gray-500 font-medium leading-relaxed italic">
                                        <strong>Context for Tester:</strong> {tc.technicalContext}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Stats */}
                      <div className="space-y-2.5">
                        <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider border-l-2 border-blue-500 pl-2">Modified Files Structural Summary</h4>
                        <pre className="p-4 bg-gray-950 font-mono text-[11px] text-gray-300 rounded-xl overflow-x-auto leading-relaxed border border-gray-800">
                          {selectedDiff.stats}
                        </pre>
                      </div>
                      {/* Code changes */}
                      <div className="space-y-2.5">
                        <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider border-l-2 border-blue-500 pl-2">Core Code Differences (git show)</h4>
                        <pre className="p-4 bg-gray-950 font-mono text-[10.5px] text-gray-300 rounded-xl overflow-x-auto leading-relaxed max-h-96 overflow-y-auto border border-gray-800">
                          {selectedDiff.diff}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalTestingCheck;
